import { useState, useRef, useCallback, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { PhysicsCanvas } from "@/components/PhysicsCanvas";
import { ForceControls } from "@/components/ForceControls";
import {
	getInitialState,
	calculateNetForce,
	SURFACES,
} from "@/lib/physics";
import type { PhysicsState, ForceConfig } from "@/lib/physics";
import { FlaskConical, TrendingUp } from "lucide-react";

export function Lesson2FMA() {
	const [state, setState] = useState<PhysicsState>(getInitialState());
	const [isRunning, setIsRunning] = useState(false);
	const [selectedSurface, setSelectedSurface] = useState("wood");
	const [config, setConfig] = useState<ForceConfig>({
		appliedForce: 30,
		mass: 5,
		frictionCoefficient: SURFACES.wood.coefficient,
		gravity: 9.8,
	});

	const { netForce, frictionForce, normalForce, weight } =
		calculateNetForce(config);
	const acceleration = config.mass > 0 ? netForce / config.mass : 0;

	const animRef = useRef<number | null>(null);
	const lastTimeRef = useRef<number>(0);

	const simulate = useCallback(
		(timestamp: number) => {
			if (lastTimeRef.current === 0) lastTimeRef.current = timestamp;
			const dt = Math.min((timestamp - lastTimeRef.current) / 1000, 0.05);
			lastTimeRef.current = timestamp;

			setState((prev) => {
				const { netForce: nf } = calculateNetForce(config);
				const acc = config.mass > 0 ? nf / config.mass : 0;
				const newVelocity = prev.velocity + acc * dt;

				return {
					position: prev.position + newVelocity * dt,
					velocity: newVelocity,
					acceleration: acc,
					time: prev.time + dt,
				};
			});

			animRef.current = requestAnimationFrame(simulate);
		},
		[config],
	);

	useEffect(() => {
		if (isRunning) {
			lastTimeRef.current = 0;
			animRef.current = requestAnimationFrame(simulate);
		} else if (animRef.current) {
			cancelAnimationFrame(animRef.current);
		}
		return () => {
			if (animRef.current) cancelAnimationFrame(animRef.current);
		};
	}, [isRunning, simulate]);

	const reset = () => {
		setIsRunning(false);
		setState(getInitialState());
		lastTimeRef.current = 0;
	};

	const updateConfig = (partial: Partial<ForceConfig>) => {
		setConfig((prev) => ({ ...prev, ...partial }));
	};

	return (
		<div className="space-y-6">
			<div className="space-y-3">
				<div className="flex items-center gap-3">
					<Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-sm px-3 py-1">
						Law 2
					</Badge>
					<h2 className="text-2xl font-bold">
						Newton's Second Law: F = ma
					</h2>
				</div>
				<blockquote className="border-l-4 border-green-500 pl-4 italic text-muted-foreground text-lg">
					"The acceleration of an object is directly proportional to
					the net force acting on it and inversely proportional to its
					mass."
				</blockquote>
			</div>

			<Card className="bg-card/50 border-muted">
				<CardContent className="pt-6 space-y-4">
					<h3 className="font-semibold text-lg">
						The Fundamental Equation
					</h3>
					<div className="text-center py-4">
						<div className="inline-block bg-muted/50 rounded-xl px-8 py-4 border border-muted">
							<span className="text-3xl font-mono font-bold">
								<span className="text-amber-400">F</span>
								<span className="text-muted-foreground">
									{" "}
									={" "}
								</span>
								<span className="text-blue-400">m</span>
								<span className="text-muted-foreground">
									{" "}
									&times;{" "}
								</span>
								<span className="text-purple-400">a</span>
							</span>
						</div>
					</div>
					<div className="grid grid-cols-3 gap-4 text-center text-sm">
						<div>
							<Badge
								variant="outline"
								className="text-amber-400 border-amber-400/30 mb-1"
							>
								F
							</Badge>
							<p className="text-muted-foreground">
								Net Force (N)
							</p>
						</div>
						<div>
							<Badge
								variant="outline"
								className="text-blue-400 border-blue-400/30 mb-1"
							>
								m
							</Badge>
							<p className="text-muted-foreground">Mass (kg)</p>
						</div>
						<div>
							<Badge
								variant="outline"
								className="text-purple-400 border-purple-400/30 mb-1"
							>
								a
							</Badge>
							<p className="text-muted-foreground">
								Acceleration (m/s²)
							</p>
						</div>
					</div>
					<p className="text-muted-foreground leading-relaxed">
						This means: double the force = double the acceleration.
						Double the mass = half the acceleration. The{" "}
						<strong className="text-foreground">net force</strong> is
						what matters -- it's the applied force minus friction and
						any other opposing forces.
					</p>
				</CardContent>
			</Card>

			<Separator />

			<div className="space-y-4">
				<h3 className="font-semibold text-lg flex items-center gap-2">
					<FlaskConical className="w-5 h-5 text-green-500" />
					Interactive Lab
				</h3>
				<p className="text-sm text-muted-foreground">
					Adjust the applied force, mass, and surface type. Watch how
					the acceleration and motion change in real time. Try to
					predict what will happen before you press Play!
				</p>

				<PhysicsCanvas
					state={state}
					config={config}
					netForce={netForce}
					frictionForce={frictionForce}
					surfaceColor={SURFACES[selectedSurface].color}
					label="F = ma Lab"
				/>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					<ForceControls
						config={config}
						onConfigChange={updateConfig}
						isRunning={isRunning}
						onToggleRun={() => setIsRunning(!isRunning)}
						onReset={reset}
						showSurfaceSelect
						selectedSurface={selectedSurface}
						onSurfaceChange={(s) => {
							setSelectedSurface(s);
							reset();
						}}
						netForce={netForce}
						frictionForce={frictionForce}
					/>

					<div className="space-y-3">
						<h4 className="font-semibold text-sm flex items-center gap-2">
							<TrendingUp className="w-4 h-4 text-green-400" />
							Live Calculations
						</h4>

						<div className="space-y-2 p-4 bg-muted/30 rounded-lg border border-muted font-mono text-sm">
							<div className="flex justify-between">
								<span className="text-muted-foreground">
									Weight (mg):
								</span>
								<span>{weight.toFixed(1)} N</span>
							</div>
							<div className="flex justify-between">
								<span className="text-muted-foreground">
									Normal Force:
								</span>
								<span>{normalForce.toFixed(1)} N</span>
							</div>
							<Separator />
							<div className="flex justify-between">
								<span className="text-green-400">
									Applied Force:
								</span>
								<span className="text-green-400">
									{config.appliedForce.toFixed(0)} N
								</span>
							</div>
							<div className="flex justify-between">
								<span className="text-red-400">
									- Friction (μ × N):
								</span>
								<span className="text-red-400">
									{Math.abs(frictionForce).toFixed(1)} N
								</span>
							</div>
							<Separator />
							<div className="flex justify-between font-bold">
								<span className="text-amber-400">
									= Net Force:
								</span>
								<span className="text-amber-400">
									{netForce.toFixed(1)} N
								</span>
							</div>
							<Separator />
							<div className="flex justify-between">
								<span className="text-muted-foreground">
									a = F/m:
								</span>
								<span className="text-purple-400 font-bold">
									{acceleration.toFixed(2)} m/s²
								</span>
							</div>
							<div className="flex justify-between">
								<span className="text-muted-foreground">
									Current velocity:
								</span>
								<span>
									{state.velocity.toFixed(2)} m/s
								</span>
							</div>
						</div>

						{netForce === 0 && config.appliedForce !== 0 && (
							<Card className="bg-amber-500/10 border-amber-500/30">
								<CardContent className="pt-3 pb-2">
									<p className="text-xs text-amber-400">
										The applied force equals friction -- the
										forces are balanced and net force is
										zero! The object won't accelerate.
									</p>
								</CardContent>
							</Card>
						)}

						{Math.abs(config.appliedForce) > 0 &&
							Math.abs(netForce) > 0 && (
								<Card className="bg-green-500/10 border-green-500/30">
									<CardContent className="pt-3 pb-2">
										<p className="text-xs text-green-400">
											{Math.abs(config.appliedForce) >
											Math.abs(frictionForce)
												? `The applied force overcomes friction! The block accelerates at ${acceleration.toFixed(2)} m/s².`
												: `Friction is stronger than the applied force. The net force opposes the motion.`}
										</p>
									</CardContent>
								</Card>
							)}
					</div>
				</div>
			</div>

			<Card className="bg-card/50 border-muted">
				<CardContent className="pt-6 space-y-3">
					<h3 className="font-semibold">Key Takeaways</h3>
					<ul className="space-y-2 text-sm text-muted-foreground">
						<li className="flex items-start gap-2">
							<span className="text-green-400 mt-0.5">1.</span>
							<strong className="text-foreground">F = ma</strong>{" "}
							relates net force, mass, and acceleration. Change
							any one and the others adjust.
						</li>
						<li className="flex items-start gap-2">
							<span className="text-green-400 mt-0.5">2.</span>
							The{" "}
							<strong className="text-foreground">
								net force
							</strong>{" "}
							is the vector sum of all forces. Friction subtracts
							from the applied force.
						</li>
						<li className="flex items-start gap-2">
							<span className="text-green-400 mt-0.5">3.</span>
							More mass means more force is needed for the same
							acceleration (inertia!).
						</li>
						<li className="flex items-start gap-2">
							<span className="text-green-400 mt-0.5">4.</span>
							If net force is zero, acceleration is zero -- the
							object maintains constant velocity (connects back to
							Law 1).
						</li>
					</ul>
				</CardContent>
			</Card>
		</div>
	);
}
